import React, {FC, useContext, useEffect, useState} from 'react';
import {Button, Col, Form, Modal, Row} from "react-bootstrap";
import {AuthContext} from "contexts";
import {isDeleteProfileAndGiftsSupported} from "enums";
import {
    AbuseWord,
    abuseWordsService,
    ActionTypes
} from "services/abuseWordsService";
import {AbuseWordModalPatternSection} from "pages/AbuseWords/components/AbuseWordModalPatternSection";
import {
    AbuseWordModalPreviewSection,
    AbuseWordPreviewDisplay
} from "pages/AbuseWords/components/AbuseWordModalPreviewSection";
import {AbuseWordModalRuleSection} from "pages/AbuseWords/components/AbuseWordModalRuleSection";
import {AbuseWordModalTestSection} from "pages/AbuseWords/components/AbuseWordModalTestSection";

const DEFAULT_WORD: AbuseWord = {
    ruleName: '',
    allowSeparateWord: true,
    patternTypes: [],
    actionType: ActionTypes.AdminCheck,
    genders: [],
    pattern: '',
    allowDelimiters: true,
    allowMultiline: true,
    allowIgnoreCase: true,
    id: 0,
    tag: null,
    language: null
}

interface Props {
    editingWord: AbuseWord | null;
    onReset: (didChange?: boolean) => void;
    appIdsSupported: boolean;
}

export const AbuseWordModal: FC<Props> = ({editingWord, onReset, appIdsSupported}) => {
    const {env, user} = useContext(AuthContext);

    const [isModalVisible, setIsModalVisible] = useState(false);
    const [word, setWord] = useState<AbuseWord>(DEFAULT_WORD);
    const [testText, setTestText] = useState('');
    const [testResult, setTestResult] = useState('');
    const [previewLimit, setPreviewLimit] = useState(100);
    const [previewLoading, setPreviewLoading] = useState(false);
    const [previewDisplay, setPreviewDisplay] = useState<AbuseWordPreviewDisplay | null>(null);

    const handleClose = (didChange?: boolean) => {
        onReset(didChange);
        setWord(DEFAULT_WORD);
        setTestResult('');
        setTestText('');
        setPreviewLimit(100);
        setPreviewDisplay(null);
        setIsModalVisible(false);
    }

    const handleSave = async () => {
        if (!env) {
            return;
        }
        if (word.id) {
            await abuseWordsService.update(word, env)
        } else {
            await abuseWordsService.add(word, env)
        }
        handleClose(true);
    }

    useEffect(() => {
        if (editingWord) {
            setWord(editingWord);
            setIsModalVisible(true);
            setPreviewLimit(100);
            setPreviewDisplay(null);
        }
    }, [editingWord]);

    useEffect(() => {
        setPreviewDisplay(null);
    }, [word.id]);

    if (!user?.isSuperAdmin || !env || !isDeleteProfileAndGiftsSupported(env)) {
        return null;
    }

    return <>
        <Button
            onClick={() => setIsModalVisible(true)}
            variant="outline-primary">
            Добавить Abuse Слово
        </Button>

        <Modal show={isModalVisible} onHide={handleClose} className="modal-90-screen" centered>
            <Modal.Header closeButton>
                <Modal.Title>{word.id ? 'Изменить Abuse правило' : 'Добавить Abuse Слово'}</Modal.Title>
            </Modal.Header>
            <Modal.Body className="modal-body-scroll pt-3 pb-4">
                <Form>
                    <div className="fw-semibold text-secondary text-uppercase small mb-2">Правило и паттерн</div>
                    <Row className="g-4 align-items-start">
                        <Col xl={5}>
                            <AbuseWordModalRuleSection word={word} setWord={setWord} app={env.app}
                                                        appIdsSupported={appIdsSupported}/>
                        </Col>
                        <Col xl={7}>
                            <AbuseWordModalPatternSection word={word} setWord={setWord}/>
                        </Col>
                    </Row>

                    <div className="fw-semibold text-secondary text-uppercase small mt-4 mb-2">Проверка</div>
                    <Row className="g-4 align-items-stretch">
                        <Col lg={6}>
                            <AbuseWordModalTestSection
                                word={word}
                                env={env}
                                testText={testText}
                                setTestText={setTestText}
                                testResult={testResult}
                                setTestResult={setTestResult}
                            />
                        </Col>
                        <Col lg={6}>
                            <AbuseWordModalPreviewSection
                                word={word}
                                env={env}
                                previewLimit={previewLimit}
                                setPreviewLimit={setPreviewLimit}
                                previewLoading={previewLoading}
                                setPreviewLoading={setPreviewLoading}
                                previewDisplay={previewDisplay}
                                setPreviewDisplay={setPreviewDisplay}
                            />
                        </Col>
                    </Row>
                </Form>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={() => handleClose()}>
                    Закрыть
                </Button>
                <Button disabled={!word.ruleName || !word.pattern || !word.genders.length || (appIdsSupported && !word.appIds?.length)} variant="primary"
                        onClick={handleSave}>
                    Сохранить
                </Button>
            </Modal.Footer>
        </Modal>
    </>
}
